/**
 * `gamification-core` — CONFIG del dominio field-check.
 *
 * OPT-IN and NON-punitive retrofit (B2B): gamification rewards fieldwork
 * well done (completed inspections, resolved findings, consistency)
 * as ENGAGEMENT, never as worker surveillance. Private leagues by
 * default (§leagues). The engine is agnostic; here we only declare the rules.
 *
 * Gamification is DERIVED from `activity` (immutable hash-chain) — see `derive.ts`.
 * It does not alter the log: it is a pure and recomputable projection.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from the `activity` feed). */
export const FIELDCHECK_EVENTS = {
  inspectionCreated: "inspection.created",
  inspectionCompleted: "inspection.completed",
  findingResolved: "finding.resolved",
} as const;

/** Streak line: days with at least one completed inspection (consistency). */
export const FIELDWORK_STREAK_LINE = "fieldwork";

export const fieldcheckGamificationConfig: GamificationConfig = {
  xp: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: 25,
    [FIELDCHECK_EVENTS.findingResolved]: 15,
    [FIELDCHECK_EVENTS.inspectionCreated]: 5,
  },
  streakLines: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: [FIELDWORK_STREAK_LINE],
  },
  counters: {
    [FIELDCHECK_EVENTS.inspectionCompleted]: ["inspections"],
    [FIELDCHECK_EVENTS.findingResolved]: ["findings_resolved"],
  },
  badges: [
    {
      id: "first-inspection",
      title: "First inspection",
      description: "Complete your first inspection.",
      when: counterAtLeast("inspections", 1),
    },
    {
      id: "ten-inspections",
      title: "Ten inspections",
      description: "Complete ten inspections.",
      when: counterAtLeast("inspections", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Complete inspections seven days in a row.",
      when: streakAtLeast(FIELDWORK_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "closer",
      title: "Closer",
      description: "Resolve ten findings.",
      when: counterAtLeast("findings_resolved", 10),
    },
  ],
  quests: [
    {
      id: "daily-inspections",
      title: "Three inspections today",
      period: "daily",
      goals: [{ metric: FIELDCHECK_EVENTS.inspectionCompleted, target: 3 }],
      bonusXp: 20,
    },
  ],
};
